import {z} from "zod";
import {mongoObjectIdSchema} from "./mongo.ts";
import {SortSchema} from "./otk-document.ts";

export const DestructionActStatuses = ["SUBMISSION_PENDING", "ACCEPTED"] as const;
export const destructionActStatusSchema = z.enum(DestructionActStatuses);
export type DestructionActStatus = z.infer<typeof destructionActStatusSchema>;

const requiredTextSchema = z.string().trim().min(1);
const calendarDateSchema = z.iso.date();
const timeSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);

export const destructionCommissionMemberSchema = z.strictObject({
    role: requiredTextSchema,
    name: requiredTextSchema,
});

export const destructionActItemSchema = z.strictObject({
    blankId: mongoObjectIdSchema,
    series: z.string(),
    number: z.string(),
});
export type DestructionActItem = z.infer<typeof destructionActItemSchema>;

export const createDestructionActSchema = z.strictObject({
    items: z.array(destructionActItemSchema).min(1),
    reason: requiredTextSchema,
    attestationStatement: requiredTextSchema,
    actDate: calendarDateSchema,
    dateFrom: calendarDateSchema,
    dateTo: calendarDateSchema,
    location: requiredTextSchema,
    commissionChair: destructionCommissionMemberSchema,
    commissionMembers: z.array(destructionCommissionMemberSchema).min(2),
    destructionPlace: requiredTextSchema,
    destructionTime: timeSchema,
    destructionMethod: requiredTextSchema,
    territorialBody: requiredTextSchema,
    copyDutyAcknowledged: z.literal(true),
    markJournalAcknowledgementStatement: requiredTextSchema.optional(),
}).superRefine((act, ctx) => {
    if (new Set(act.items.map(({blankId}) => blankId.toLowerCase())).size !== act.items.length) {
        ctx.addIssue({
            code: "custom",
            path: ["items"],
            message: "Blank ids must be unique",
        });
    }
    if (act.dateFrom > act.dateTo) {
        ctx.addIssue({
            code: "custom",
            path: ["dateTo"],
            message: "dateTo must not be earlier than dateFrom",
        });
    }
});

export type CreateDestructionAct = z.infer<typeof createDestructionActSchema>;

const acknowledgementSchema = z.object({
    statement: z.string(),
    byUser: z.string(),
    at: z.iso.datetime(),
});

export const otkDestructionActSchema = z.object({
    id: mongoObjectIdSchema,
    subjectId: mongoObjectIdSchema,
    actNumber: z.string(),
    status: destructionActStatusSchema,
    items: z.array(destructionActItemSchema).min(1),
    blankReasons: z.record(mongoObjectIdSchema, z.string()),
    actDate: calendarDateSchema,
    dateFrom: calendarDateSchema,
    dateTo: calendarDateSchema,
    location: z.string(),
    commissionChair: destructionCommissionMemberSchema,
    commissionMembers: z.array(destructionCommissionMemberSchema),
    destructionPlace: z.string(),
    destructionTime: timeSchema,
    destructionMethod: z.string(),
    attestation: acknowledgementSchema,
    copyToTerritorialBody: acknowledgementSchema.extend({
        territorialBody: z.string(),
    }).omit({statement: true}),
    markJournalAcknowledgement: acknowledgementSchema.nullable(),
    createdAt: z.iso.datetime(),
    acceptedAt: z.iso.datetime().nullable(),
    submitRetryAt: z.iso.datetime().optional(),
});

export type OtkDestructionAct = z.infer<typeof otkDestructionActSchema>;

export const otkDestructionActsListQuerySchema = z.object({
    status: destructionActStatusSchema.default("ACCEPTED"),
    actDateFrom: calendarDateSchema.optional(),
    actDateTo: calendarDateSchema.optional(),
    pageSize: z.coerce.number().int().min(5).max(50).default(25),
    cursor: z.string().optional(),
    sort: SortSchema.default("DESC"),
}).refine(
    ({actDateFrom, actDateTo}) => !actDateFrom || !actDateTo || actDateFrom <= actDateTo,
    {path: ["actDateTo"], message: "actDateTo must not be earlier than actDateFrom"},
);

export type OtkDestructionActsListQuery = z.infer<typeof otkDestructionActsListQuerySchema>;

export interface DestructionActsPageResult {
    data: OtkDestructionAct[];
    nextPage: string | null;
}
